/**
 * XtermTerminal
 *
 * Problem: xterm's Viewport constructor schedules a setTimeout that calls
 * _innerRefresh → reads `renderer.dimensions`. If the terminal is disposed
 * (StrictMode fake-unmount) before that timeout fires, `renderer` is already
 * torn down and the read throws "Cannot read properties of undefined (reading
 * 'dimensions')".
 *
 * Additionally, calling term.open() on an element with zero size (e.g. inside
 * a hidden MUI Tab panel) causes the same crash immediately.
 *
 * Fixes:
 * 1. Defer term.open() via requestAnimationFrame until the container has real
 *    layout dimensions (width > 0 && height > 0).
 * 2. Track a `disposed` flag; if the component unmounts before open() runs,
 *    dispose the terminal immediately instead of opening it.
 * 3. Wrap the deferred fit() in a try/catch so the StrictMode-orphaned
 *    Viewport setTimeout never propagates.
 */
import React, { useEffect, useRef } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";

function hasSize(el) {
  if (!el) return false;
  const { width, height } = el.getBoundingClientRect();
  return width > 0 && height > 0;
}

export default function XtermTerminal({ onReady }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Build terminal but do NOT open yet
    const term = new Terminal({
      cursorBlink: true,
      convertEol: true,
      scrollback: 3000,
      fontSize: 13,
      rendererType: "dom",
    });
    const fit = new FitAddon();
    term.loadAddon(fit);

    let disposed = false;
    let rafId = null;

    const doFit = () => {
      try { fit.fit(); } catch {}
    };

    const openAndNotify = () => {
      if (disposed) {
        // Component already unmounted before we could open — just discard.
        try { term.dispose(); } catch {}
        return;
      }

      term.open(el);

      // First fit after a further rAF to let the browser finalise layout
      requestAnimationFrame(() => {
        if (disposed) return;
        doFit();
      });

      const ro = new ResizeObserver(() => { if (!disposed) doFit(); });
      ro.observe(el);

      // Store ro on term so cleanup can reach it
      term.__ro = ro;

      onReady?.({ term, fit, safeFit: doFit });
    };

    // Wait until the container has real dimensions before opening.
    // In practice it's ready immediately (same rAF), but hidden Tab panels
    // may need a second frame.
    const waitForSize = () => {
      if (disposed) return;
      if (hasSize(el)) {
        openAndNotify();
      } else {
        rafId = requestAnimationFrame(waitForSize);
      }
    };

    rafId = requestAnimationFrame(waitForSize);

    return () => {
      disposed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      try { term.__ro?.disconnect(); } catch {}
      // Dispose the terminal. The xterm Viewport has an internal setTimeout
      // that may fire after dispose and attempt to read renderer.dimensions —
      // that throws, but only as an unhandled error in the old console. We
      // can't suppress it from outside xterm, but wrapping dispose in a
      // try/catch at least prevents further cascade.
      try { term.dispose(); } catch {}
    };
  }, []); 

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: 420,
        minHeight: 320,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.12)",
        background: "#0b0f14",
      }}
    />
  );
}